// Merge LLM BOM-correction output onto the seeded prior draft so a partial
// model response still yields a complete replace list.

import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { DictatedDraftLine, DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";

function softKey(line: DictatedDraftLine): string {
  const mat =
    line.match.kind === "matched"
      ? line.match.material_id
      : line.material_name_heard.trim().toLowerCase();
  return `${mat}::${normalizeSlotLabel(line.slot_label)}`;
}

/**
 * Ensure the proposed draft is a full material-line replacement:
 * - Prefer LLM lines in order
 * - Re-append any prior lines the model dropped (same material+slot)
 * - New materials from the LLM are kept
 */
export function ensureFullBomReplacementDraft(
  prior: DictatedRecipeDraft,
  proposed: DictatedRecipeDraft,
): DictatedRecipeDraft {
  const nextLines = [...proposed.lines];
  const covered = new Set(nextLines.map(softKey));

  for (const p of prior.lines) {
    const key = softKey(p);
    if (covered.has(key)) continue;
    // Model omitted this line — keep prior verbatim (full-replace completeness).
    nextLines.push({ ...p });
    covered.add(key);
  }

  return {
    ...proposed,
    code: proposed.code || prior.code,
    name: proposed.name || prior.name,
    kind: proposed.kind || prior.kind,
    tags: proposed.tags?.length ? proposed.tags : prior.tags,
    lines: nextLines,
  };
}
